// Items, crates and the entity step (§3). The crate pipeline (seeded schedule and placement, drop
// telegraph, head bonk, open, lifetime), rubber-banded crate rolls, item grants, every item use
// (§3.3), and the per-entity step that dispatches to the item modules (peel.ts, bomb.ts,
// plunger.ts), ghost pebbles and props.
//
// Predict mode (§18.2): item uses change the local bean (charges, PUNCH_WINDUP, FLOAT, Big Shake)
// but spawn nothing; crate spawns, landings' head bonks and openings are server-only; entities
// only advance ballistically (plus the local bean's own peel slip).
import { CRATE_WEIGHTS, DT, NO_SLOT, TUNE, VOID } from '../constants';
import { EV, emit } from '../events';
import { BS, EK, ItemKind, ModeId } from '../ids';
import { MAPS } from '../maps/index';
import { createGroundHit } from '../maps/mapTypes';
import { MODES } from '../modes/index';
import { rand, randIntRange, randRange } from '../rng';
import type { Bean, Entity, InputFrame, Vec2, World } from '../types';
import { startFloat } from './balloon';
import { hasBody } from './bean';
import { stepBomb, throwBombItem } from './bomb';
import { bodyCollides } from './collision';
import { isRemoved, removeEntity, spawnEntity } from './entities';
import { stepPebble } from './ghosts';
import { placePeel, stepPeel } from './peel';
import { firePlunger, stepPlunger } from './plunger';
import { dropHeight, headBonk, isProp, stepProp } from './props';
import { canUseItem } from './stateTable';
import { isPredicting, isScoringMode } from './world';

export { findEntity, removeEntity, spawnEntity } from './entities';

/** Charges granted per item (§3.3). */
export function itemCharges(kind: ItemKind): number {
  switch (kind) {
    case ItemKind.BANANA: return TUNE.banana.charges;
    case ItemKind.BOMB: return TUNE.bomb.charges;
    case ItemKind.GLOVE: return TUNE.glove.charges;
    case ItemKind.BALLOON: return TUNE.balloon.charges;
    case ItemKind.PLUNGER: return TUNE.plunger.charges;
    case ItemKind.SHAKE: return TUNE.shake.charges;
    default: return 0;
  }
}

export function giveItem(w: World, slot: number, kind: ItemKind): void {
  const b = w.beans[slot];
  b.item = kind;
  b.charges = itemCharges(kind);
  emit(w, EV.ITEM_GET, slot, kind);
}

/** CRATE_WEIGHTS column (§3.1): 0 leader, 1 top half, 2 bottom half. */
const LEADER = 0;
const TOP_HALF = 1;
const BOTTOM_HALF = 2;

/**
 * Rubber-band column by match rank among active beans. Crown Grab and the non-scoring modes do
 * not use rank, and neither does round 1 (nobody has scored yet): both use the top-half column.
 */
function rankColumn(w: World, slot: number): number {
  const mode = w.cfg.modeId;
  if (mode === ModeId.CROWN_GRAB || !isScoringMode(mode)) return TOP_HALF;
  const scores = w.cfg.matchScores;
  const mine = scores[slot] ?? 0;
  let count = 0;
  let higher = 0;
  let anyScore = false;
  for (const b of w.beans) {
    if (!b.active) continue;
    const s = scores[b.slot] ?? 0;
    count++;
    if (s > mine) higher++;
    if (s !== 0) anyScore = true;
  }
  if (!anyScore) return TOP_HALF;
  if (higher === 0) return LEADER;
  return (higher + 1) * 2 <= count ? TOP_HALF : BOTTOM_HALF;
}

/** Rubber-banded crate roll (§3.1) over the round's item pool; Bomb Party crates are always bombs. */
export function rollCrateItem(w: World, slot: number): ItemKind {
  const pool = w.rules.itemPool;
  if (pool.length === 0) return ItemKind.NONE;
  if (w.rules.bombOnlyCrates) return ItemKind.BOMB;
  const col = rankColumn(w, slot);
  let total = 0;
  for (const kind of pool) total += CRATE_WEIGHTS[kind][col];
  let r = rand(w) * total;
  for (const kind of pool) {
    r -= CRATE_WEIGHTS[kind][col];
    if (r < 0) return kind;
  }
  return pool[pool.length - 1];
}

/**
 * Input phase (§3.2): Item uses one charge when the state allows that item (§2.14; a falling bean's
 * Plunger only above `plunger.fallingMinZ`) and the mode does not block items (crown holder).
 * Banana places a peel, Bomb throws one, Glove winds up a punch, Balloon floats, Plunger fires and
 * Big Shake grows the bean (its stats derive from `shakeTicks`).
 */
export function useItem(w: World, b: Bean, pressed: boolean, frame: InputFrame | null = null): void {
  if (!pressed || b.item === ItemKind.NONE || b.charges <= 0 || !canUseItem(b.state, b.item)) return;
  if (b.state === BS.FALLING && b.z <= TUNE.plunger.fallingMinZ) return;
  if (MODES[w.cfg.modeId].itemsBlocked(w, b.slot)) return;
  const kind = b.item;
  const ax = frame ? frame.ax : 0;
  const ay = frame ? frame.ay : 0;
  emit(w, EV.ITEM_USE, b.slot, kind);
  switch (kind) {
    case ItemKind.BANANA:
      placePeel(w, b);
      break;
    case ItemKind.BOMB:
      throwBombItem(w, b, ax, ay);
      break;
    case ItemKind.GLOVE:
      b.state = BS.PUNCH_WINDUP;
      b.st = 0;
      b.lungeHitMask = 0;
      emit(w, EV.PUNCH_WINDUP, b.slot);
      break;
    case ItemKind.BALLOON:
      startFloat(w, b, false);
      break;
    case ItemKind.PLUNGER:
      firePlunger(w, b, ax, ay);
      break;
    case ItemKind.SHAKE:
      b.shakeTicks = TUNE.shake.ticks;
      emit(w, EV.SHAKE_START, b.slot);
      break;
    default:
      break;
  }
  if (--b.charges <= 0) {
    b.item = ItemKind.NONE;
    b.charges = 0;
  }
}

/** The lowest slot able to open crate `e`: a free body with an empty slot, no crown, not carrying (§3.1). */
function crateOpener(w: World, e: Entity): number {
  for (const b of w.beans) {
    if (!bodyCollides(b) || b.state === BS.CARRYING || b.item !== ItemKind.NONE || b.holdsCrown) continue;
    if (Math.abs(b.z - e.z) > TUNE.hitDzDefault) continue;
    if (Math.hypot(b.pos.x - e.pos.x, b.pos.y - e.pos.y) <= TUNE.crate.radius) return b.slot;
  }
  return NO_SLOT;
}

/** Crate: falls during its telegraph, lands with a head bonk, then waits to be opened or poofs. */
function stepCrate(w: World, e: Entity): void {
  const c = TUNE.crate;
  if (e.t < c.dropTicks) {
    const t = (c.dropTicks - e.t) * DT;
    e.z = 0.5 * w.rules.gravity * t * t;
    e.vz = -w.rules.gravity * t;
    return;
  }
  if (e.t === c.dropTicks) {
    const g = MAPS[w.cfg.mapId].groundAt(w, e.pos.x, e.pos.y);
    e.z = g === VOID ? 0 : g;
    e.vz = 0;
    e.timer = c.lifeTicks;
    if (!isPredicting(w)) headBonk(w, e.pos.x, e.pos.y);
    return;
  }
  if (isPredicting(w)) return;
  if (--e.timer <= 0) {
    removeEntity(w, e.id);
    return;
  }
  const slot = crateOpener(w, e);
  if (slot === NO_SLOT) return;
  const kind = rollCrateItem(w, slot);
  emit(w, EV.CRATE_OPEN, slot, kind, e.id, 0, e.pos.x, e.pos.y);
  removeEntity(w, e.id);
  if (kind !== ItemKind.NONE) giveItem(w, slot, kind);
}

/** Entities in step order this tick (a copy: steps may remove any entity, removed ones are skipped). */
const stepList: Entity[] = [];

/**
 * Step phase 6: ages every entity, then runs crates, peels, bombs, plungers, pebbles and props, in
 * spawn order. Crown physics belongs to the Crown Grab mode.
 */
export function stepEntities(w: World): void {
  const server = !isPredicting(w);
  stepList.length = 0;
  for (const e of w.ents) stepList.push(e);
  for (const e of stepList) {
    if (isRemoved(e)) continue;
    e.t++;
    switch (e.kind) {
      case EK.CRATE: stepCrate(w, e); break;
      case EK.PEEL: stepPeel(w, e); break;
      case EK.BOMB: stepBomb(w, e); break;
      case EK.PLUNGER: stepPlunger(w, e); break;
      case EK.PEBBLE: stepPebble(w, e); break;
      default: if (server && isProp(e)) stepProp(w, e); break;
    }
  }
  stepList.length = 0;
}

const hit = createGroundHit();
const cratePoint: Vec2 = { x: 0, y: 0 };

/** Rejection-sampling budget per wanted crate candidate (bounds the RNG draws on sparse maps). */
const CRATE_TRIES_PER_CANDIDATE = 8;

/**
 * Crate placement (§3.1): seeded points inside the map bounds are drawn until
 * `TUNE.crate.candidates` of them are eligible (on ground, at least `minEdgeDist` from any edge and
 * outside hazard footprints; at most 8 draws per candidate), and the eligible one closest to the
 * centroid of the beans in play wins. False when no candidate qualifies.
 */
function pickCratePoint(w: World): boolean {
  const map = MAPS[w.cfg.mapId];
  let cx = 0;
  let cy = 0;
  let n = 0;
  for (const b of w.beans) {
    if (!hasBody(b)) continue;
    cx += b.pos.x;
    cy += b.pos.y;
    n++;
  }
  if (n > 0) {
    cx /= n;
    cy /= n;
  } else {
    cx = map.center.x;
    cy = map.center.y;
  }
  let best = Infinity;
  let valid = 0;
  for (let i = 0; i < TUNE.crate.candidates * CRATE_TRIES_PER_CANDIDATE && valid < TUNE.crate.candidates; i++) {
    const x = randRange(w, map.bounds.minX, map.bounds.maxX);
    const y = randRange(w, map.bounds.minY, map.bounds.maxY);
    if (map.groundAt(w, x, y) === VOID || map.crateExclusion(w, x, y)) continue;
    if (map.nearestGround(w, x, y, hit).dist < TUNE.crate.minEdgeDist) continue;
    valid++;
    const d2 = (x - cx) ** 2 + (y - cy) ** 2;
    if (d2 < best) {
      best = d2;
      cratePoint.x = x;
      cratePoint.y = y;
    }
  }
  return best < Infinity;
}

/**
 * Step phase 12 (server only): the crate schedule of scoring rounds with items. The first crate is
 * due at `TUNE.crate.firstTick`, then every crateMin–crateMax ticks; a due crate is skipped while
 * the field already holds `crateMaxCount`.
 */
export function stepCrates(w: World): void {
  if (!isScoringMode(w.cfg.modeId) || w.rules.itemPool.length === 0 || w.over || w.tick < w.crateNextTick) return;
  w.crateNextTick = w.tick + randIntRange(w, w.rules.crateMin, w.rules.crateMax);
  let crates = 0;
  for (const e of w.ents) if (e.kind === EK.CRATE) crates++;
  if (crates >= w.rules.crateMaxCount || !pickCratePoint(w)) return;
  const e = spawnEntity(w, EK.CRATE, NO_SLOT, cratePoint.x, cratePoint.y, dropHeight(w, TUNE.crate.dropTicks));
  emit(w, EV.CRATE_DROP, NO_SLOT, NO_SLOT, e.id, 0, e.pos.x, e.pos.y);
}
